import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { ParticipantId, WallParticipant, WallGroup } from '@walls/store';

interface GroupEditorOverlayProps {
  editingParticipantIds: ParticipantId[];
  participants: WallParticipant[];
  saveLabel?: string;
  group?: WallGroup;
  groupName: string;
  title?: string;
  onCancel: () => void;
  onSave: () => void;
  onGroupNameChange: (groupName: string) => void;
  onToggleParticipant: (participantId: ParticipantId) => void;
}

export function GroupEditorOverlay({
  editingParticipantIds,
  participants,
  saveLabel = 'Save',
  group,
  groupName,
  title = 'Edit group',
  onCancel,
  onSave,
  onGroupNameChange,
  onToggleParticipant,
}: GroupEditorOverlayProps) {
  const labelName = group?.name ?? (groupName.trim() || 'new group');

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="group-editor-title"
      sx={{
        bgcolor: '#fff',
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb',
          flex: '0 0 auto',
          p: 2,
        }}
      >
        <Typography id="group-editor-title" variant="subtitle2" noWrap>
          {title}
        </Typography>
        <Tooltip title="Cancel editing">
          <IconButton
            size="small"
            aria-label={`Cancel editing ${labelName}`}
            onClick={onCancel}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack spacing={1.5} sx={{ flex: '0 0 auto', p: 2 }}>
        <TextField
          value={groupName}
          onChange={(event) => onGroupNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onSave();
            }
          }}
          label="Group name"
          size="small"
          fullWidth
          autoFocus
          inputProps={{ 'aria-label': `Name ${labelName}` }}
        />
      </Stack>
      <Divider />
      <Stack spacing={0.25} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', p: 1 }}>
        {participants.map((participant) => (
          <Box
            key={participant.id}
            component="label"
            sx={{
              alignItems: 'center',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              gap: 0.5,
              px: 0.5,
              py: 0.25,
              '&:hover': { bgcolor: '#f3f4f6' },
            }}
          >
            <Checkbox
              size="small"
              checked={editingParticipantIds.includes(participant.id)}
              onChange={() => onToggleParticipant(participant.id)}
              inputProps={{
                'aria-label': `${participant.name} in ${labelName}`,
              }}
            />
            <Typography variant="body2" noWrap>
              {participant.name}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ flex: '0 0 auto', p: 1.5 }}
      >
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon fontSize="small" />}
          onClick={onSave}
          disabled={!groupName.trim()}
          sx={{ textTransform: 'none' }}
        >
          {saveLabel}
        </Button>
      </Stack>
    </Box>
  );
}
